import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { ProTablePlus } from '@bedrock/eed-react';
import { IProTablePlusColumn } from '@bedrock/eed-react/lib/components/ProTablePlus/interface';
import { Tag, Upload } from '@bedrock/components';
import { TagColor } from '@bedrock/components/lib/Tag';
import dayjs from 'dayjs';
import { tableColumnWidthToRem } from '@mpc/common/src/utils';
import AiTag from '@mpc/common/src/components/AiTag';
// TODO: [API-CONFIRM] 后端历史记录专用接口待确认，暂用内部 list API 按 biz_id 过滤代替
import { outsourceV2AdvancePriceCheckKolQuoteOperation } from 'services/api';
import { ProCard } from '@bedrock/eed-react';

export interface SupplierKolQuoteRoundVO {
  id: number;
  round: number;
  record_no: string;
  blob_id: number;
  file_name: string;
  state: string;
  upload_at: string;
}

interface Props {
  advancePriceCheckId?: number;
}

export interface QuoteHistoryTableRef {
  refresh: () => void;
}

const STATE_MAP: Record<string, { color?: TagColor; text: string; isAi?: boolean }> = {
  AI_CHECKING: { text: '智能核价中', isAi: true },
  PENDING_CONFIRM: { color: 'light-orange', text: '待商务确认核价' },
  PENDING_PLAN_CONFIRM_DARU: { color: 'light-blue', text: '待策划确认达人' },
  CONFIRMED: { color: 'light-green', text: '已核价' },
  VOIDED: { color: 'light-red', text: '已作废' },
  CLOSED: { color: 'light-grey', text: '已关闭' },
  NOT_UPLOADED: { color: 'light-grey', text: '未上传' },
};

export const QuoteHistoryTable = forwardRef<QuoteHistoryTableRef, Props>(({ advancePriceCheckId }, ref) => {
  const { data, loading, refresh } = useRequest(
    // TODO: [API-CONFIRM] ListForm 无 advance_price_check_id 字段，待后端提供专用接口后替换
    () => outsourceV2AdvancePriceCheckKolQuoteOperation({ advance_price_check_id: advancePriceCheckId! }),
    {
      ready: !!advancePriceCheckId,
      refreshDeps: [advancePriceCheckId],
    },
  );

  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  const historyData: SupplierKolQuoteRoundVO[] = useMemo(
    () => ((data as any)?.data ?? []) as SupplierKolQuoteRoundVO[],
    [data],
  );

  const columns: IProTablePlusColumn[] = useMemo(
    () =>
      [
        {
          title: '轮次',
          dataIndex: 'round',
          ...tableColumnWidthToRem({ width: 72 }),
          render: (v: number) => (v != null ? <span className="font-semibold">第{v}轮</span> : '--'),
        },
        {
          title: '核价记录单号',
          dataIndex: 'record_no',
          ...tableColumnWidthToRem({ width: 160 }),
          render: (v: string) => v || '--',
        },
        {
          title: '状态',
          dataIndex: 'state',
          ...tableColumnWidthToRem({ width: 120 }),
          render: (v: string) => {
            const stateKey = (v || 'NOT_UPLOADED') as keyof typeof STATE_MAP;
            const stateInfo = STATE_MAP[stateKey] ?? STATE_MAP.NOT_UPLOADED;
            return stateInfo.isAi ? (
              <AiTag variant="filled-light" text={stateInfo.text} size="xSmall" />
            ) : (
              <Tag color={stateInfo.color} text={stateInfo.text} size="xSmall" />
            );
          },
        },
        {
          title: '附件',
          dataIndex: 'file_name',
          ...tableColumnWidthToRem({ width: 320 }),
          align: 'left',
          render: (v: string, r: SupplierKolQuoteRoundVO) => {
            if (!r.file_name) return '--';
            return (
              <Upload
                className="advance-price-check-upload"
                fileList={[{ uid: '1', name: r.file_name, url: '' }]}
                showRemoveIcon={false}
                showDownloadIcon={false}
                trigger={<></>}
              />
            );
          },
        },
      ] as IProTablePlusColumn[],
    [],
  );

  return (
    <ProCard title="历史核价" level={1}>
      <ProTablePlus columns={columns} data={historyData} loading={loading} pagination={false} scroll={{ x: true }} />
    </ProCard>
  );
});

QuoteHistoryTable.displayName = 'QuoteHistoryTable';

export default QuoteHistoryTable;
